import {CommonModule} from '@angular/common';
import {Component, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {FormControl, ReactiveFormsModule, Validators} from '@angular/forms';
import {Router, RouterModule} from '@angular/router';
import {Subject, takeUntil} from 'rxjs';
import {LoadingService} from '../../services/loading.service';


import {environment} from '../../../../environments/environment';
import {DeviceService} from '../../services/device.service';
import {IndexedDbAnswersService} from '../../services/indexeddb-answers.service';
import {IndexedDbFailedAnswersService} from '../../services/indexeddb-failed-answers.service';
import {IndexedDbResponseSendService} from '../../services/indexeddb-response-send.service';
import {IndexedDbUnitService} from '../../services/indexeddb-unit.service';
import {PollService} from '../../services/poll.service';
import {AlertService} from '../../services/alert.service';
import {IndexedDbIncentiveService} from "../../services/indexeddb-incentive.service";
import { IPartialUnit } from 'projects/lib-ui/src/shared/interfaces/unit';
import {
  IPollResponse,
  IPollSubmissionResponse,
} from 'projects/lib-ui/src/shared/interfaces/poll-responses';
import { DeviceBlockStateService } from '../../services/device-block-state.service';

@Component({
  selector: 'app-footer',
  standalone: true,
  imports: [RouterModule, CommonModule, ReactiveFormsModule],
  providers: [DeviceService],
  templateUrl: './footer.component.html',
  styleUrls: ['./footer.component.scss'],
})
export class FooterComponent implements OnInit, OnDestroy {
  private readonly deviceService = inject(DeviceService);
  private readonly router = inject(Router);
  private readonly alertService = inject(AlertService);
  private readonly loadingService = inject(LoadingService);

  private readonly indexedDbAnswersService = inject(IndexedDbAnswersService);
  private readonly indexedDbFailedAnswersService = inject(
    IndexedDbFailedAnswersService
  );
  private readonly indexedDbResponseSendService = inject(
    IndexedDbResponseSendService
  );
  private readonly pollService = inject(PollService);
  private readonly indexedDbUnitService = inject(IndexedDbUnitService);
  private readonly indexedDbIncentiveService = inject(IndexedDbIncentiveService)
  private readonly deviceBlockStateService = inject(DeviceBlockStateService);
  private readonly unsubscribe$ = new Subject();

  networkInterval: any;


  hasNetwork: boolean = false;


  @Input() unit!: IPartialUnit;

  answers: IDBValidKey[] = [];
  answersSent: IDBValidKey[] = [];
  answersNotSent: IDBValidKey[] = [];

  password = new FormControl('', Validators.required);
  modalToUnLink = false;

  ngOnInit(): void {
    this.networkInterval = setInterval(() => {
      this.getCountAnswers();
      this.getAnswersNotSent();
    }, 15000);

    this.getCountAnswers();
    this.getAnswersNotSent();
    this.getAllResponseSend();
  }

  ngOnDestroy(): void {
    clearInterval(this.networkInterval);
    this.unsubscribe$.next(null);
    this.unsubscribe$.complete();
  }

  getCountAnswers(): void {
    this.indexedDbAnswersService.getAllAnswersIds().then((answers) => {
      this.answers = answers;
        this.handlerAnswers();
    });
  }

  getAnswersNotSent(): void {
    this.indexedDbFailedAnswersService
      .getAllFailedAnswersIds()
      .then((answers) => {
        this.answersNotSent = answers;
      });
  }

  handlerAnswers(): void {
    this.hasNetwork = navigator.onLine;

    if (navigator.onLine && this.answers.length) {
      this.indexedDbAnswersService
        .getAnswerById(this.answers[0] as string)
        .then((answer) => {
          if (answer) this.sendAnswer(answer);
        });
    } else if (navigator.onLine && this.answersNotSent.length) {
      this.indexedDbFailedAnswersService
        .getFailedAnswerById(this.answersNotSent[0] as string)
        .then((answer) => {
          if (answer) this.sendAnswer(answer);
        });
    } else {
    }
  }

  sendAnswer(pollResponse: IPollResponse): void {
    this.pollService
      .sendPollResponse(pollResponse)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (response) => {
          if (!response.success) {
            if (!this.answersNotSent.some((value) => value === pollResponse.id)) {
              this.addFailedAnswers(pollResponse);
            }
            return;
          }

          this.deleteAnswersById(pollResponse.id);
          this.deleteFailedAnswersById(pollResponse.id);
          this.addAnswerSend(pollResponse.id);

          this.handleBlockSignal(response.data);
        },
        error: () => {
          if (!this.answersNotSent.some((value) => value === pollResponse.id)) {
            this.addFailedAnswers(pollResponse);
          }
        },
      });
  }

  private handleBlockSignal(
    response: IPollSubmissionResponse | null
  ): void {
    const suspiciousActivity = response?.suspiciousActivity;
    if (!suspiciousActivity || suspiciousActivity.alertLevel === null) {
      return;
    }

    this.deviceBlockStateService.setBlockState(
      suspiciousActivity,
      'poll-submission'
    );
  }

  addFailedAnswers(pollResponse: IPollResponse): void {
    this.indexedDbFailedAnswersService
      .addFailedAnswers(pollResponse)
      .then(() => {
        this.deleteAnswersById(pollResponse.id);
      });
  }

  addAnswerSend(id: string): void {
    this.indexedDbResponseSendService
      .addResponseSend({id})
      .then(() => this.getAllResponseSend());
  }

  getAllResponseSend(): void {
    this.indexedDbResponseSendService.getAllResponseSend().then((res) => {
      this.answersSent = res;
    });
  }

  deleteAnswersById(id: string): void {
    if (this.answers.some((value) => value === id)) {
      this.indexedDbAnswersService.deleteAnswersById(id).then(() => {
        this.answers = this.answers.filter((answer) => answer !== id);
        this.handlerAnswers();
      });
    }
  }

  deleteFailedAnswersById(id: string): void {
    if (this.answersNotSent.some((value) => value === id)) {
      this.indexedDbFailedAnswersService
        .deleteFailedAnswersById(id)
        .then(() => {
          this.answersNotSent = this.answersNotSent.filter(
            (answer) => answer !== id
          );
          this.handlerAnswers();
        });
    }
  }

  unLinkDevice(): void {
    if (this.password.value !== environment.pass) {
      this.alertService.danger('Senha incorreta!');
      return;
    }

    this.loadingService.showLoading();

    this.deviceService
      .unLinkDevice(this.unit.device.id)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: () => {
          this.indexedDbClearDB();
        },
        error: () => {
          this.indexedDbClearDB();
        },
      });
  }

  indexedDbClearDB(): void {
    this.deviceBlockStateService.clearBlockState();
    this.indexedDbUnitService
      .clearDB()
      .then(() => {
        this.indexedDbIncentiveService.clearDB().then(() => {
        });
        this.router.navigate([`/setup`]).then(() => {
        });
      });
  }
}
